/**
 * Cart Drawer Gifts & Rewards — Ladypolitan
 *
 * Performance-optimized:
 *   - Optimistic UI: qty/price/subtotal update on click, before server roundtrip
 *   - Per-line debounce (150 ms) + AbortController → 5 rapid clicks = 1 request
 *   - Response reuse: /cart/change.js response feeds reconcile, no separate /cart.js fetch
 *   - Skip full HTML rebuild for pure qty changes; rebuild only on add/remove or server-side
 *     discount mutation
 *   - Diff-based DOM updates avoid innerHTML thrashing
 *
 * Pricing of gift lines is handled by a Shopify Function reading line item
 * property _gift = "true". This script never touches prices.
 */
(function () {
  'use strict';

  var SEL_DRAWER       = '#CartDrawer';
  var SEL_REWARDS      = '[data-cart-rewards]';
  var SEL_PICKER       = '[data-gift-picker]';
  var SEL_GIFT_CTA     = '[data-gift-cta]';
  var SEL_TRASH        = '[data-cart-trash]';
  var SEL_GIFT_REMOVE  = '[data-cart-gift-remove]';
  var SEL_QTY_INC      = '[data-cart-qty-increment]';
  var SEL_QTY_DEC      = '[data-cart-qty-decrement]';
  var SEL_QTY_WRAP     = '[data-cart-qty]';
  var SEL_CART_ITEM    = '[data-cart-item]';
  var SEL_CART_COUNT   = '[data-cart-count]';
  var SEL_SUBTOTAL     = '[data-subtotal]';
  var SEL_LINE_PRICE   = '[data-line-price]';
  var SEL_LINE_COMPARE = '[data-line-compare]';
  var GIFT_PROP_KEY    = '_gift';
  var GIFT_PROP_VAL    = 'true';
  var DEBOUNCE_MS      = 150;

  /* ---------- Utilities ---------- */

  function fmtMoney(cents) {
    if (window.theme && theme.Currency && theme.settings && theme.settings.moneyFormat) {
      try { return theme.Currency.formatMoney(cents, theme.settings.moneyFormat); } catch (e) {}
    }
    var euros = (cents / 100).toFixed(2).replace('.', ',');
    return euros.replace(/,00$/, '') + ' €';
  }

  function isGiftLine(item) {
    if (!item || !item.properties) return false;
    var props = item.properties;
    if (Array.isArray(props)) {
      for (var i = 0; i < props.length; i++) {
        if (props[i] && props[i][0] === GIFT_PROP_KEY && String(props[i][1]) === GIFT_PROP_VAL) return true;
      }
      return false;
    }
    return String(props[GIFT_PROP_KEY] || '') === GIFT_PROP_VAL;
  }

  function findGiftLine(cart) {
    if (!cart || !cart.items) return null;
    for (var i = 0; i < cart.items.length; i++) {
      if (isGiftLine(cart.items[i])) return cart.items[i];
    }
    return null;
  }

  function paidSubtotalCents(cart) {
    if (!cart || !cart.items) return 0;
    var sum = 0;
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      if (isGiftLine(it)) continue;
      sum += (it.final_price || it.price || 0) * (it.quantity || 0);
    }
    return sum;
  }

  function paidCount(cart) {
    if (!cart || !cart.items) return 0;
    var n = 0;
    for (var i = 0; i < cart.items.length; i++) {
      if (isGiftLine(cart.items[i])) continue;
      n += cart.items[i].quantity || 0;
    }
    return n;
  }

  /* ---------- DOM-only fast paths ---------- */

  /**
   * Reads paid subtotal directly from the DOM (data-unit-price × data-qty on each item).
   * Used for instant optimistic recompute without waiting for cart.js.
   */
  function paidSubtotalFromDOM() {
    var items = document.querySelectorAll(SEL_CART_ITEM);
    var sum = 0;
    items.forEach(function (item) {
      if (item.classList.contains('lp-item--gift')) return;
      var unit = parseInt(item.getAttribute('data-unit-price'), 10) || 0;
      var qty  = parseInt(item.getAttribute('data-qty'), 10) || 0;
      sum += unit * qty;
    });
    return sum;
  }

  function paidCountFromDOM() {
    var items = document.querySelectorAll(SEL_CART_ITEM);
    var n = 0;
    items.forEach(function (item) {
      if (item.classList.contains('lp-item--gift')) return;
      var qty = parseInt(item.getAttribute('data-qty'), 10) || 0;
      n += qty;
    });
    return n;
  }

  /* ---------- Optimistic patches ---------- */

  var TRASH_SVG = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="square" stroke-linejoin="miter" aria-hidden="true" focusable="false"><path d="M3 4h10M6 4V2.5h4V4M4.5 4l.6 9.2c.04.6.5 1.05 1.1 1.05h3.6c.6 0 1.07-.45 1.1-1.05L11.5 4M7 7v5M9 7v5"/></svg>';
  var MINUS_SVG = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="square" stroke-linejoin="miter" aria-hidden="true" focusable="false"><path d="M3 8h10"/></svg>';

  /**
   * Swap the decrement button between TRASH (qty=1) and MINUS (qty>1) states.
   * Without this, after a 1→2 increment the trash icon would remain and the user
   * couldn't decrement back to 1 without deleting the line.
   */
  function swapDecrementButton(qtyWrap, newQty, key) {
    if (!qtyWrap) return;
    var firstBtn = qtyWrap.querySelector('button');
    if (!firstBtn) return;
    if (newQty <= 1) {
      // Become trash
      if (firstBtn.hasAttribute('data-cart-qty-decrement')) firstBtn.removeAttribute('data-cart-qty-decrement');
      firstBtn.setAttribute('data-cart-trash', '');
      firstBtn.setAttribute('data-key', key);
      firstBtn.className = 'is-trash';
      firstBtn.setAttribute('aria-label', 'Entfernen');
      if (firstBtn.innerHTML.indexOf('M3 4h10') === -1) firstBtn.innerHTML = TRASH_SVG;
    } else {
      // Become minus
      if (firstBtn.hasAttribute('data-cart-trash')) firstBtn.removeAttribute('data-cart-trash');
      firstBtn.setAttribute('data-cart-qty-decrement', '');
      firstBtn.setAttribute('data-key', key);
      firstBtn.className = '';
      firstBtn.setAttribute('aria-label', 'Weniger');
      if (firstBtn.innerHTML.indexOf('M3 8h10') === -1 || firstBtn.innerHTML.indexOf('M6 4V2.5') !== -1) firstBtn.innerHTML = MINUS_SVG;
    }
  }

  function patchLineQty(key, newQty) {
    var item = document.querySelector(SEL_CART_ITEM + '[data-key="' + key + '"]');
    if (!item) return null;
    var qtyWrap = item.querySelector(SEL_QTY_WRAP);
    var qtyNum  = item.querySelector('.lp-qty__num');
    var priceEl = item.querySelector(SEL_LINE_PRICE);
    var compareEl = item.querySelector(SEL_LINE_COMPARE);
    var unit = parseInt(item.getAttribute('data-unit-price'), 10) || 0;
    // Prefer the unified data-unit-strike (set by Liquid from variant compare OR original_price);
    // fall back to data-unit-compare for backwards compat with cached HTML.
    var unitStrike = 0;
    if (qtyWrap) {
      unitStrike = parseInt(qtyWrap.getAttribute('data-unit-strike'), 10) || 0;
      if (unitStrike === 0) unitStrike = parseInt(qtyWrap.getAttribute('data-unit-compare'), 10) || 0;
    }
    var prevQty = parseInt(item.getAttribute('data-qty'), 10) || 0;

    item.setAttribute('data-qty', String(newQty));
    if (qtyWrap) qtyWrap.setAttribute('data-qty', String(newQty));
    if (qtyNum)  qtyNum.textContent = String(newQty);
    // Keep the hidden form input in sync — otherwise a form submit to /cart
    // (e.g., the checkout button) would send the stale initial qty and clobber
    // the server cart state right before the redirect to /checkout.
    var updateInputOpt = item.querySelector('input[name="updates[]"]');
    if (updateInputOpt) updateInputOpt.value = String(newQty);

    // "Mit Code: X €" discount-applied state — optimistically scale linearly.
    //   - Percentage code → discounted_unit constant → linear scaling is exact.
    //   - Fixed-amount code → discounted_unit drifts with qty (Shopify allocates per-line).
    //     Linear is slightly off but reconciles within ~250 ms; better UX than stale text.
    var discountedEl = item.querySelector('[data-line-discounted]');
    var unitOriginal = qtyWrap ? (parseInt(qtyWrap.getAttribute('data-unit-original'), 10) || 0) : 0;
    var hasCodeDiscount = !!discountedEl;

    if (priceEl && priceEl.textContent !== 'Gratis') {
      if (hasCodeDiscount && unitOriginal > 0) {
        // top = original_unit × qty (always linear)
        priceEl.textContent = fmtMoney(unitOriginal * newQty);
      } else {
        priceEl.textContent = fmtMoney(unit * newQty);
      }
    }
    if (discountedEl) {
      // unit = final_price per unit (after discount); linear scaling for optimistic feedback
      discountedEl.textContent = fmtMoney(unit * newQty);
    }
    if (compareEl && unitStrike > 0) {
      compareEl.textContent = fmtMoney(unitStrike * newQty);
    }
    // NOTE: button swap (minus ↔ trash) is intentionally NOT done optimistically.
    // It only runs after the server confirms qty in reconcileFromCart() / rebuilt HTML.
    // Reason: optimistic swap causes accidental removal on rapid double-taps —
    // tap 1 decrements 2→1 and swaps to trash; tap 2 hits the trash and removes.
    // Keeping minus until server confirms means the decrement click handler is
    // idempotent at qty=1 (clamped to min 1), so double-taps stop cleanly at 1.

    return { unit: unit, prevQty: prevQty };
  }

  function patchSubtotal(newCents) {
    var el = document.querySelector(SEL_SUBTOTAL);
    if (!el) return;
    el.setAttribute('data-cents', String(newCents));
    el.textContent = fmtMoney(newCents);
  }

  function patchCount(newCount) {
    var el = document.querySelector(SEL_CART_COUNT);
    if (el) el.textContent = String(newCount);
  }

  /**
   * Compute total savings from DOM only (no cart needed) — used by optimistic updates
   * so the "Du sparst" row stays in sync with qty changes before the server responds.
   * For each non-gift line:
   *   - line_strike_savings = (unit_strike - unit_final) × qty (if strike > final)
   *   - covers both variant-compare savings and discount-allocated-per-line savings
   * Cart-level fixed-amount codes that DON'T allocate per-line aren't reflected here;
   * those reconcile via reconcileFromCart's authoritative computeSavings() once the
   * server response arrives.
   */
  function savingsFromDOM() {
    var total = 0;
    document.querySelectorAll(SEL_CART_ITEM).forEach(function (el) {
      if (el.classList.contains('lp-item--gift')) return;
      var qtyWrap = el.querySelector(SEL_QTY_WRAP);
      if (!qtyWrap) return;
      var finalP = parseInt(el.getAttribute('data-unit-price'), 10) || 0;
      var qty = parseInt(el.getAttribute('data-qty'), 10) || 0;
      var strike = parseInt(qtyWrap.getAttribute('data-unit-strike'), 10) || 0;
      var compare = parseInt(qtyWrap.getAttribute('data-unit-compare'), 10) || 0;
      var original = parseInt(qtyWrap.getAttribute('data-unit-original'), 10) || 0;
      // Strike beats compare beats original for the highest-reference price
      var ref = strike;
      if (ref === 0) ref = compare;
      if (ref < original) ref = original;
      if (ref > finalP && qty > 0) {
        total += (ref - finalP) * qty;
      }
    });
    return total;
  }

  function patchSavings(cents) {
    var drawer = document.querySelector(SEL_DRAWER);
    if (!drawer) return;
    var savingsRow = drawer.querySelector('.cart__total-savings');
    if (!savingsRow) return;
    if (cents > 0) {
      savingsRow.style.display = 'flex';
      var amt = savingsRow.querySelector('.cart__total-savings-amount');
      if (amt) amt.textContent = '−' + fmtMoney(cents);
    } else {
      savingsRow.style.display = 'none';
    }
  }

  function syncEphemeralFromDOM() {
    var paid = paidSubtotalFromDOM();
    patchSubtotal(paid);
    patchCount(paidCountFromDOM());
    patchSavings(savingsFromDOM());
    // Rewards bar uses paid subtotal + thresholds from data-attrs — recompute live.
    syncRewardsFromCents(paid);
  }

  /* ---------- Rewards bar ---------- */

  var lastRewardsHTML = '';

  function syncRewardsFromCents(paid) {
    var drawer = document.querySelector(SEL_DRAWER);
    if (!drawer) return;
    var rewards = drawer.querySelector(SEL_REWARDS);
    if (!rewards) return;

    var freeshipCents = parseInt(rewards.getAttribute('data-freeship-cents'), 10) || 0;
    var giftCents     = parseInt(rewards.getAttribute('data-gift-cents'), 10) || 0;
    var barMaxCents   = parseInt(rewards.getAttribute('data-bar-max-cents'), 10) || (giftCents > 0 ? giftCents : freeshipCents);
    var mode          = rewards.getAttribute('data-mode') || (giftCents > 0 ? 'dual' : 'single');
    rewards.setAttribute('data-paid-subtotal', String(paid));

    var shipReached = paid >= freeshipCents && freeshipCents > 0;
    var giftReached = paid >= giftCents && giftCents > 0;
    var fullReached = mode === 'dual' ? giftReached : shipReached;
    rewards.classList.toggle('is-full', fullReached);

    var msg = rewards.querySelector('.lp-rewards__msg');
    if (msg) {
      var checkSvg = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="square" stroke-linejoin="miter" aria-hidden="true"><path d="M3 8.5l3.5 3.5L13 5"/></svg>';
      var html;
      if (paid <= 0) {
        html = '<span>Noch <b class="accent">' + fmtMoney(freeshipCents) + '</b> bis <b>kostenlosem Versand</b>.</span>';
      } else if (!shipReached) {
        html = '<span>Nur noch <b class="accent">' + fmtMoney(freeshipCents - paid) + '</b> bis zum <b>kostenlosen Versand</b>.</span>';
      } else if (mode === 'dual' && !giftReached && giftCents > 0) {
        html = '<span><span class="done">' + checkSvg + 'Versand frei.</span><span class="lp-rewards__line">Nur noch <b class="accent">' + fmtMoney(giftCents - paid) + '</b> bis zum <b>Gratis-Geschenk</b>.</span></span>';
      } else {
        var shipLabel = mode === 'dual' ? 'Versand frei.' : 'Du hast kostenlosen Versand freigeschaltet.';
        html = '<span><span class="done">' + checkSvg + shipLabel + '</span> ';
        if (mode === 'dual' && giftCents > 0) html += '<span class="done">' + checkSvg + 'Geschenk freigeschaltet.</span><span class="lp-rewards__line"><b>Wähle dein Gratis-Geschenk unten.</b></span>';
        html += '</span>';
      }
      if (html !== lastRewardsHTML) {
        msg.innerHTML = html;
        lastRewardsHTML = html;
      }
    }

    var fill = rewards.querySelector('.lp-rewards__fill');
    if (fill && barMaxCents > 0) {
      var pct = Math.min(100, (paid / barMaxCents) * 100);
      fill.style.width = pct + '%';
    }
    var milestones = rewards.querySelectorAll('.lp-rewards__milestone');
    if (milestones.length >= 1) milestones[0].classList.toggle('is-reached', shipReached);
    if (milestones.length >= 2) milestones[1].classList.toggle('is-reached', giftReached);

    var steps = rewards.querySelectorAll('.lp-step');
    if (steps.length === 2 && freeshipCents > 0 && giftCents > 0) {
      var shipPct = Math.min(1, paid / freeshipCents);
      var giftPct = paid <= freeshipCents ? 0 : Math.min(1, (paid - freeshipCents) / Math.max(1, (giftCents - freeshipCents)));
      var f1 = steps[0].querySelector('.lp-step__fill');
      var f2 = steps[1].querySelector('.lp-step__fill');
      if (f1) f1.style.transform = 'scaleX(' + shipPct + ')';
      if (f2) f2.style.transform = 'scaleX(' + giftPct + ')';
      steps[0].classList.toggle('is-reached', shipReached);
      steps[1].classList.toggle('is-reached', giftReached);
    }
  }

  /* ---------- Gift picker ---------- */

  /* Das Gift-Dock sitzt fix im Bottom-Stack des Drawers. Eingeklappt ist es eine
     44-px-Leiste, aufgeklappt zeigt es die Geschenk-Karten. Auf-/Zuklappen laeuft
     ueber .is-open am Section-Element (CSS animiert grid-template-rows). */
  function setGiftDockOpen(picker, open) {
    if (!picker) return;
    picker.classList.toggle('is-open', !!open);
    var toggle = picker.querySelector('[data-gift-toggle]');
    if (toggle) toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  /* Kopfzeile spiegelt den Zustand: gesperrt → Restbetrag, frei → Aufforderung,
     Geschenk gewaehlt → Bestaetigung. */
  function updateGiftBarText(picker, unlocked, hasSelection, missingCents) {
    var titleSpan = picker.querySelector('.lp-giftpicker__title span');
    var hint = picker.querySelector('[data-gift-hint]');
    var thresholdCents = parseInt(picker.getAttribute('data-gift-threshold-cents'), 10) || 0;

    var title, hintText;
    if (hasSelection) {
      title = 'Dein Geschenk ist dabei';
      hintText = 'Ändern';
    } else if (unlocked) {
      title = 'Wähle dein Gratis-Geschenk';
      hintText = 'Freigeschaltet';
    } else {
      title = 'Gratis-Geschenk ab ' + fmtMoney(thresholdCents);
      hintText = 'Noch ' + fmtMoney(Math.max(0, missingCents));
    }

    if (titleSpan && titleSpan.textContent !== title) titleSpan.textContent = title;
    if (hint && hint.textContent !== hintText) hint.textContent = hintText;
  }

  function updateGiftPicker(cart) {
    var drawer = document.querySelector(SEL_DRAWER);
    if (!drawer) return;
    var picker = drawer.querySelector(SEL_PICKER);
    if (!picker) return;

    var threshold = parseInt(picker.getAttribute('data-gift-threshold-cents'), 10) || 0;
    var paid = cart ? paidSubtotalCents(cart) : paidSubtotalFromDOM();
    var unlocked = threshold > 0 && paid >= threshold;
    var giftLine = cart ? findGiftLine(cart) : null;
    var selectedProductId = giftLine ? giftLine.product_id : null;
    var giftKey = giftLine ? giftLine.key : '';

    var prevLocked = picker.getAttribute('data-last-locked');
    var nextLocked = unlocked ? 'false' : 'true';
    if (prevLocked !== nextLocked) {
      picker.classList.toggle('is-locked', !unlocked);
      picker.setAttribute('data-last-locked', nextLocked);
      // Bewusst KEIN automatisches Auf-/Zuklappen: das Dock startet offen und
      // bleibt es, bis die Kundin selbst zuklappt. Ein Auto-Toggle wuerde ihr
      // beim Aendern der Menge die gerade getroffene Entscheidung wegnehmen.
    }

    // Ohne cart-Objekt ist giftKey immer leer → Auswahl aus dem DOM-Attribut lesen,
    // sonst wuerde die Leiste faelschlich "Waehle dein Gratis-Geschenk" anzeigen.
    var hasSelForBar = cart ? (giftKey !== '') : (picker.getAttribute('data-has-selection') === 'true');
    updateGiftBarText(picker, unlocked, hasSelForBar, threshold - paid);

    picker.setAttribute('data-paid-subtotal-cents', String(paid));
    picker.setAttribute('data-selected-gift-key', giftKey);

    // Exactly ONE gift allowed: once one is in the cart, lock all other cards. Switching
    // requires an explicit "Geschenk wechseln" / removing the current gift first.
    var hasSelection = giftKey !== '';
    picker.setAttribute('data-has-selection', hasSelection ? 'true' : 'false');

    var PLUS_SVG  = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="square" stroke-linejoin="miter" focusable="false"><path d="M8 3v10M3 8h10"/></svg>';
    var CHECK_SVG = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="square" stroke-linejoin="miter"><path d="M3 8.5l3.5 3.5L13 5"/></svg>';

    var cards = picker.querySelectorAll('[data-gift-card]');
    cards.forEach(function (card) {
      var pid = parseInt(card.getAttribute('data-product-id'), 10);
      var isSel = selectedProductId !== null && pid === selectedProductId;
      // Ausverkauftes Geschenk bleibt IMMER gesperrt — sonst würde es beim Erreichen der
      // Schwelle wieder aktivierbar (Server kann es eh nicht hinzufügen → Support-Chaos).
      var soldout = card.getAttribute('data-soldout') === 'true';
      var prevSel = card.getAttribute('data-last-sel');
      var nextSel = isSel ? 'true' : 'false';
      if (prevSel === nextSel) {
        // Only refresh disabled state on button, not full markup
        var btnFast = card.querySelector(SEL_GIFT_CTA);
        if (btnFast) btnFast.disabled = soldout || !unlocked || (hasSelection && !isSel);
        return;
      }
      card.setAttribute('data-last-sel', nextSel);
      card.classList.toggle('is-sel', isSel);

      var btn = card.querySelector(SEL_GIFT_CTA);
      if (btn) {
        btn.disabled = soldout || !unlocked || (hasSelection && !isSel);
        btn.setAttribute('data-action', isSel ? 'remove' : 'select');
        if (isSel && giftKey) btn.setAttribute('data-gift-key', giftKey);
        else btn.removeAttribute('data-gift-key');
        var indicator = btn.querySelector('.lp-gcard__indicator');
        if (indicator) indicator.innerHTML = isSel ? CHECK_SVG : PLUS_SVG;
      }
    });
  }

  /* ---------- Reconcile from authoritative server response ---------- */

  function computeSavings(cart) {
    if (!cart || !cart.items) return 0;
    // Match Liquid: (original_total_price − total_price) gives all discount savings.
    var total = (cart.original_total_price || 0) - (cart.total_price || 0);
    // Variant-compare savings: /cart.js does NOT include variant.compare_at_price in
    // line items (Shopify only exposes price/original_price/final_price). Read it from
    // the DOM where Liquid emitted it as `data-unit-compare` per line.
    document.querySelectorAll(SEL_CART_ITEM).forEach(function (el) {
      var qtyWrap = el.querySelector(SEL_QTY_WRAP);
      if (!qtyWrap) return;
      var compare = parseInt(qtyWrap.getAttribute('data-unit-compare'), 10) || 0;
      var original = parseInt(qtyWrap.getAttribute('data-unit-original'), 10) || 0;
      var qty = parseInt(qtyWrap.getAttribute('data-qty'), 10) || 0;
      if (compare > 0 && original > 0 && compare > original) {
        total += (compare - original) * qty;
      }
    });
    return total;
  }

  function reconcileFromCart(cart, opts) {
    if (!cart) return;
    var authoritative = !!(opts && opts.authoritative); // unsere eigenen Reconciles ignorieren Dirty-Guard
    patchSubtotal(cart.total_price);

    var drawer = document.querySelector(SEL_DRAWER);
    if (drawer) {
      var savingsRow = drawer.querySelector('.cart__total-savings');
      var savings = computeSavings(cart);
      if (savingsRow) {
        savingsRow.style.display = savings > 0 ? 'flex' : 'none';
        var amt = savingsRow.querySelector('.cart__total-savings-amount');
        if (amt) amt.textContent = '−' + fmtMoney(savings);
      }
    }

    // Reconcile each existing line — server final_price wins (Shopify Function may have applied discounts).
    // PER-LINE SKIP: if a line has an in-flight pending qty change, don't overwrite its optimistic
    // state — the pending request's own response will reconcile it when it completes. This prevents
    // visible flicker/revert when an external sync (cart:open, observer, app event) runs in parallel.
    cart.items.forEach(function (line) {
      // Pending (in-flight) ODER kürzlich geklickt (dirty) + externer Reconcile → Line in Ruhe lassen,
      // damit der frisch gesetzte Wert nicht zurückspringt.
      if (pendingChanges.has(line.key)) return;
      if (!authoritative && isDirty(line.key)) return;
      var el = document.querySelector(SEL_CART_ITEM + '[data-key="' + line.key + '"]');
      if (!el) return;
      el.setAttribute('data-unit-price', String(line.final_price));
      el.setAttribute('data-qty', String(line.quantity));
      var qtyWrap = el.querySelector(SEL_QTY_WRAP);
      if (qtyWrap) {
        qtyWrap.setAttribute('data-qty', String(line.quantity));
        qtyWrap.setAttribute('data-unit-price', String(line.final_price));
      }
      var qtyNum = el.querySelector('.lp-qty__num');
      if (qtyNum) qtyNum.textContent = String(line.quantity);
      // Authoritative server qty → form input stays valid for any subsequent form submission.
      var updateInputRec = el.querySelector('input[name="updates[]"]');
      if (updateInputRec) updateInputRec.value = String(line.quantity);
      var priceEl = el.querySelector(SEL_LINE_PRICE);
      var discountedEl = el.querySelector('[data-line-discounted]');
      var hasCodeDiscountSrv = !!discountedEl;
      if (priceEl && priceEl.textContent !== 'Gratis') {
        if (hasCodeDiscountSrv && line.original_price) {
          priceEl.textContent = fmtMoney(line.original_price * line.quantity);
        } else {
          priceEl.textContent = fmtMoney(line.final_price * line.quantity);
        }
      }
      if (discountedEl) {
        discountedEl.textContent = fmtMoney(line.final_price * line.quantity);
      }
      // Keep data-unit-original in sync so optimistic patches use authoritative values
      if (qtyWrap && line.original_price != null) {
        qtyWrap.setAttribute('data-unit-original', String(line.original_price));
      }
      // Durchgestrichener Statt-Preis. Der Unit-Wert kommt aus Liquid und ist
      // mengenunabhaengig, skaliert also linear. Muss hier mit, seit der Reconcile
      // ohne HTML-Rebuild laeuft — sonst bliebe die Zeile auf dem alten Wert stehen.
      var compareRec = el.querySelector(SEL_LINE_COMPARE);
      if (compareRec && qtyWrap) {
        var unitStrikeRec = parseInt(qtyWrap.getAttribute('data-unit-strike'), 10) || 0;
        if (unitStrikeRec === 0) unitStrikeRec = parseInt(qtyWrap.getAttribute('data-unit-compare'), 10) || 0;
        if (unitStrikeRec > 0) compareRec.textContent = fmtMoney(unitStrikeRec * line.quantity);
      }
      // Keep trash↔minus button in sync with authoritative quantity
      swapDecrementButton(qtyWrap, line.quantity, line.key);
    });

    patchCartDiscounts(cart);
    patchCount(paidCount(cart));
    syncRewardsFromCents(paidSubtotalCents(cart));
    updateGiftPicker(cart);
    toggleEmptyState(cart);
  }

  /* Cart-Level-Rabatte direkt aus dem Cart-JSON patchen statt den ganzen
     Items-Block neu zu rendern. Ohne das waere bei jeder Mengenaenderung ein
     HTML-Rebuild noetig, nur damit diese eine Zeile stimmt. */
  function patchCartDiscounts(cart) {
    var box = document.querySelector('.cart__discounts');
    if (!box) return;
    var apps = (cart && cart.cart_level_discount_applications) || [];

    if (apps.length === 0) {
      box.classList.add('hide');
      var rowEmpty = box.querySelector('.lp-summary__row');
      if (rowEmpty) rowEmpty.remove();
      return;
    }

    var parts = apps.map(function (d) {
      return (d.title || d.code || '') + ' (−' + fmtMoney(d.total_allocated_amount || 0) + ')';
    }).join(' ');

    var row = box.querySelector('.lp-summary__row');
    if (!row) {
      row = document.createElement('div');
      row.className = 'lp-summary__row';
      row.setAttribute('style', 'font-size:12px;padding:8px 20px;');
      row.innerHTML = '<span class="label"></span><span class="val"></span>';
      box.appendChild(row);
    }
    var labelEl = row.querySelector('.label');
    // Label kommt aus dem servergerenderten Markup; nur setzen, wenn es fehlt.
    if (labelEl && !labelEl.textContent.trim()) labelEl.textContent = 'Rabatte';
    var valEl = row.querySelector('.val');
    if (valEl && valEl.textContent.trim() !== parts) valEl.textContent = parts;
    box.classList.remove('hide');
  }

  /* ---------- Empty state ---------- */

  function isCartEmptyFromDOM() {
    return document.querySelectorAll(SEL_CART_ITEM).length === 0;
  }

  function toggleEmptyState(cart) {
    var drawer = document.querySelector(SEL_DRAWER);
    if (!drawer) return;
    // Authoritative: count actual rendered item nodes in DOM. This handles the case where
    // an external add-to-cart from PDP populates items but never fires a cart:updated
    // event we listen to — the DOM has items, so we hide empty regardless of cart param.
    var domHasItems = !isCartEmptyFromDOM();
    var cartHasItems = cart && (cart.item_count || 0) > 0;
    var isEmpty = !domHasItems && !cartHasItems;

    var rewards   = drawer.querySelector(SEL_REWARDS);
    var picker    = drawer.querySelector(SEL_PICKER);
    var reassure  = drawer.querySelector('.lp-reassure');
    var footer    = drawer.querySelector('.lp-footer');
    var empty     = drawer.querySelector('[data-cart-empty]');
    var scroll    = drawer.querySelector('.lp-drawer__scroll');

    [rewards, picker, reassure, footer].forEach(function (el) {
      if (!el) return;
      el.style.display = isEmpty ? 'none' : '';
    });

    if (isEmpty) {
      if (!empty && scroll) {
        empty = document.createElement('div');
        empty.className = 'lp-empty';
        empty.setAttribute('data-cart-empty', '');
        empty.innerHTML =
          '<div class="lp-empty__inner">' +
          '<h3 class="lp-empty__title">Dein Warenkorb ist leer</h3>' +
          '<p class="lp-empty__sub">Entdecke unsere Produkte und finde dein neues Lieblingsstück.</p>' +
          '<nav class="lp-empty__nav" aria-label="Kategorie-Navigation">' +
          '<a href="/collections/new-in" class="lp-empty__navlink js-drawer-close">NEW IN</a>' +
          '<a href="/collections/all" class="lp-empty__navlink js-drawer-close">BEKLEIDUNG</a>' +
          '<a href="/collections/schuhe" class="lp-empty__navlink js-drawer-close">SCHUHE</a>' +
          '<a href="/collections/sale" class="lp-empty__navlink lp-empty__navlink--sale js-drawer-close">SALE</a>' +
          '</nav>' +
          '</div>';
        scroll.appendChild(empty);
      } else if (empty) empty.style.display = '';
    } else if (empty) empty.style.display = 'none';
  }

  /* ---------- Watch external item additions (PDP add-to-cart) ----------
     theme.CartForm.cartMarkup replaces .cart__items but doesn't always dispatch
     cart:updated. We observe the drawer scroll container and trigger a full server
     sync whenever items get added/removed so the footer / rewards / empty state
     never go out of sync. */
  var observerSyncScheduled = false;
  function scheduleObserverSync() {
    if (observerSyncScheduled) return;
    observerSyncScheduled = true;
    // Coalesce rapid mutations into one server sync
    setTimeout(function () {
      observerSyncScheduled = false;
      // A qty change in flight will reconcile itself — don't read a stale snapshot meanwhile.
      if (pendingChanges.size > 0) return;
      var gen = cartGeneration;
      getCart().then(function (cart) {
        if (cartGeneration !== gen || pendingChanges.size > 0) return; // superseded → discard
        reconcileFromCart(cart);
      }).catch(function () {
        if (cartGeneration !== gen || pendingChanges.size > 0) return;
        toggleEmptyState(null);
        syncEphemeralFromDOM();
      });
    }, 50);
  }

  function attachItemsObserver() {
    var drawer = document.querySelector(SEL_DRAWER);
    if (!drawer) return;
    // Observe the entire drawer scroll area so we catch external replacements
    // of [data-products] as a whole, not just its children.
    var scroll = drawer.querySelector('.lp-drawer__scroll') || drawer;
    var observer = new MutationObserver(function (mutations) {
      // Only react to item-level additions/removals, not text-content updates
      // that our own optimistic patches do (those don't add/remove nodes inside .cart__items)
      var meaningful = mutations.some(function (m) {
        if (m.type !== 'childList') return false;
        var changed = [].concat([].slice.call(m.addedNodes || []), [].slice.call(m.removedNodes || []));
        return changed.some(function (n) {
          if (n.nodeType !== 1) return false;
          return n.matches && (n.matches(SEL_CART_ITEM) || n.matches('.cart__items') || n.querySelector && n.querySelector(SEL_CART_ITEM));
        });
      });
      if (meaningful) scheduleObserverSync();
    });
    observer.observe(scroll, { childList: true, subtree: true });
  }

  /* ---------- Cart API (XHR) ---------- */

  // WICHTIG: Cart-Requests laufen über XMLHttpRequest, NICHT über window.fetch/theme.cart.
  // App-Scripts auf dem Store patchen window.fetch (lb-selleasy.js, shop_events_listener) und
  // korrumpieren intermittierend Cart-Requests → 400/leere Antwort → Mengen-Stepper sprang zurück.
  // XHR wird von diesen Wrappern NICHT abgefangen. Rejected sauber bei non-2xx/kaputtem JSON,
  // damit der Aufrufer (processLine) gezielt retrien + den echten Cart nachladen kann.
  function xhrJSON(method, url, body) {
    return new Promise(function (resolve, reject) {
      try {
        var x = new XMLHttpRequest();
        x.open(method, url, true);
        x.withCredentials = true;
        x.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
        if (body != null) x.setRequestHeader('Content-Type', 'application/json');
        x.onload = function () {
          if (x.status >= 200 && x.status < 300) {
            try { resolve(JSON.parse(x.responseText)); }
            catch (e) { reject(new Error('bad-json')); }
          } else {
            // Status und Body mitgeben: Shopify liefert bei 422 (Lagerbestand
            // erschoepft) eine fertige, lokalisierte Meldung mit, die sonst
            // verloren geht und die Mengenaenderung stumm zuruecksetzt.
            var err = new Error('http-' + x.status);
            err.status = x.status;
            try { err.body = JSON.parse(x.responseText); } catch (e2) {}
            reject(err);
          }
        };
        x.onerror = function () { reject(new Error('network')); };
        x.send(body != null ? body : null);
      } catch (e) { reject(e); }
    });
  }

  // HTML-GET via XHR (umgeht ebenfalls die App-fetch-Wrapper). Für den Voll-Refresh der Items.
  function xhrText(url) {
    return new Promise(function (resolve, reject) {
      try {
        var x = new XMLHttpRequest();
        x.open('GET', url, true);
        x.withCredentials = true;
        x.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
        x.onload = function () { (x.status >= 200 && x.status < 300) ? resolve(x.responseText) : reject(new Error('http-' + x.status)); };
        x.onerror = function () { reject(new Error('network')); };
        x.send(null);
      } catch (e) { reject(e); }
    });
  }

  function changeRequest(key, qty, signal) {
    var url = ((window.theme && theme.routes && theme.routes.cartChange) || '/cart/change.js') + '?t=' + Date.now();
    return xhrJSON('POST', url, JSON.stringify({ id: key, quantity: qty }));
  }

  function addRequest(variantId, properties, signal) {
    var url = (window.theme && theme.routes && theme.routes.cartAdd) || '/cart/add.js';
    return xhrJSON('POST', url, JSON.stringify({ items: [{ id: variantId, quantity: 1, properties: properties || {} }] }));
  }

  function getCart() {
    var url = ((window.theme && theme.routes && theme.routes.cart) || '/cart.js') + '?t=' + Date.now();
    return xhrJSON('GET', url, null);
  }

  function rebuildDrawerHTML() {
    // Triggers theme.CartForm.buildCart → /cart?view=ajax fetch + DOM replace.
    // Mark this as OUR build so the cart:build listener in defensiveSync doesn't fire a
    // redundant /cart.js fetch (self-loop). theme.CartForm's handler runs synchronously
    // within the dispatch; release the flag on the next tick once the event has propagated.
    suppressBuildSync++;
    document.dispatchEvent(new CustomEvent('cart:build'));
    setTimeout(function () { if (suppressBuildSync > 0) suppressBuildSync--; }, 0);
  }

  // Voll-Refresh des Drawers aus dem server-gerenderten Cart-Markup (/cart?view=ajax) via XHR.
  // Ersetzt .cart__items + .cart__discounts → korrektes Entfernen, Preise, Discounts, ohne
  // Abhängigkeit von theme.CartForm.buildCart (das fetch nutzt und von App-Wrappern gestört wird).
  function rebuildDrawerItems() {
    var url = ((window.theme && theme.routes && theme.routes.cartPage) || '/cart');
    url += (url.indexOf('?') === -1 ? '?' : '&') + 'view=ajax&t=' + Date.now();
    return xhrText(url).then(function (html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var freshItems = doc.querySelector('.cart__items');
      var freshDisc = doc.querySelector('.cart__discounts');
      var drawer = document.querySelector(SEL_DRAWER);
      if (!drawer) return;
      var items = drawer.querySelector('.cart__items');
      if (items && freshItems) {
        items.innerHTML = freshItems.innerHTML;
        ['data-count', 'data-cart-savings', 'data-cart-subtotal'].forEach(function (a) {
          if (freshItems.hasAttribute(a)) items.setAttribute(a, freshItems.getAttribute(a));
        });
      }
      var disc = drawer.querySelector('.cart__discounts');
      if (disc && freshDisc) { disc.innerHTML = freshDisc.innerHTML; disc.className = freshDisc.className; }
    });
  }

  // Kontext: Klick auf der /cart-Seite (#CartPageForm) vs. Drawer.
  function isCartPageContext(item) {
    if (item && item.closest) return !!item.closest('#CartPageForm');
    return !!document.querySelector('#CartPageForm');
  }

  // Einheitlicher Abschluss nach einer Cart-Mutation: den GANZEN sichtbaren Cart server-echt laden.
  //  - /cart-Seite: voller Reload (bulletproof – Items/Entfernen/Summe/Sparst/Progress/Gift korrekt).
  //  - Drawer: Items per XHR neu rendern + Chrome (Summe, „Du sparst", Progress-Bar, Gift, Empty)
  //    aus dem autoritativen Cart aktualisieren.
  /**
   * Nach einer bestaetigten Server-Mutation den sichtbaren Cart nachziehen.
   *
   * Reine Mengenaenderungen brauchen KEINEN HTML-Rebuild: reconcileFromCart patcht
   * Menge, Zeilenpreise, Statt-Preise, Rabattzeile, Zwischensumme, Ersparnis,
   * Counter, Rewards-Bar und Gift-Picker direkt aus dem Cart-JSON.
   *
   * Der frueher bedingungslose rebuildDrawerItems() ersetzte .cart__items per
   * innerHTML und hat damit bei JEDEM +/- die Scrollposition auf 0 gesetzt und den
   * Fokus vom geklickten Button genommen — das war der "Reset" beim Mengenswitcher.
   * needsHtmlRebuild() war fuer genau diesen Fall vorgesehen, wurde aber nie
   * aufgerufen. Neu gerendert wird jetzt nur noch, wenn sich die Zeilenanzahl
   * aendert (Artikel entfernt, Geschenk dazu/weg) — und selbst dann werden
   * Scrollposition und Fokus wiederhergestellt.
   */
  function settleCart(cart, item) {
    if (isCartPageContext(item)) { window.location.reload(); return; }

    var reconcile = function () {
      if (cart && cart.items) reconcileFromCart(cart, { authoritative: true });
    };

    var rebuild = function () {
      var scroller = document.querySelector('.lp-drawer__scroll');
      var savedScroll = scroller ? scroller.scrollTop : 0;

      // Fokus merken, damit Tastatur- und Schnellklick-Bedienung den Rebuild ueberlebt.
      var focusKey = null, focusRole = null;
      var ae = document.activeElement;
      if (ae && ae.closest) {
        var focusItem = ae.closest(SEL_CART_ITEM);
        if (focusItem) {
          focusKey = focusItem.getAttribute('data-key');
          if (ae.closest(SEL_QTY_INC)) focusRole = SEL_QTY_INC;
          else if (ae.closest(SEL_QTY_DEC)) focusRole = SEL_QTY_DEC;
        }
      }

      var restore = function () {
        reconcile();
        if (scroller) scroller.scrollTop = savedScroll;
        if (focusKey && focusRole) {
          // Kann fehlen, wenn die Zeile gerade entfernt wurde oder Minus zu Papierkorb
          // gewechselt ist — dann bleibt der Fokus einfach, wo er ist.
          var back = document.querySelector(SEL_CART_ITEM + '[data-key="' + focusKey + '"] ' + focusRole);
          if (back && back.focus) back.focus();
        }
      };

      rebuildDrawerItems().then(restore).catch(restore);
    };

    if (needsHtmlRebuild(cart)) { rebuild(); return; }

    reconcile();

    // Sicherheitsnetz: Wenn der sichtbare Stand nach dem Reconcile NICHT zum Server
    // passt, doch neu rendern. Damit kann das Auslassen des HTML-Rebuilds nie eine
    // falsche Menge stehen lassen — im Normalfall kostet der Check nichts.
    if (!domMatchesCartQty(cart)) rebuild();
  }

  /* Stimmt die sichtbare Menge jeder Zeile mit dem Server-Cart ueberein? */
  function domMatchesCartQty(cart) {
    var scope = document.querySelector(SEL_DRAWER) || document;
    var lines = (cart && cart.items) ? cart.items : [];
    for (var i = 0; i < lines.length; i++) {
      var el = scope.querySelector(SEL_CART_ITEM + '[data-key="' + lines[i].key + '"]');
      if (!el) return false;
      var want = String(lines[i].quantity);
      if (el.getAttribute('data-qty') !== want) return false;
      var num = el.querySelector('.lp-qty__num');
      if (num && num.textContent.trim() !== want) return false;
    }
    return true;
  }

  /**
   * Cancel the per-line debounce on every pending change and fire its request
   * immediately. Returns a Promise that resolves once all pending /cart/change.js
   * responses have come back. Used before form submission (checkout button) so
   * the server cart is fully up-to-date before the form POST clobbers it.
   */
  function flushPendingChanges() {
    if (pendingChanges.size === 0) return Promise.resolve();
    var promises = [];
    pendingChanges.forEach(function (entry, key) {
      if (entry.timer) {
        clearTimeout(entry.timer);
        entry.timer = null;
      }
      // Don't fire again if an in-flight request already started (timer was already
      // null and ctrl exists with a pending fetch). Only the timed-but-not-yet-fired
      // ones need a kick. For simplicity here, we always re-fire since aborting and
      // re-firing is idempotent at the server (same id+qty).
      var signal = entry.ctrl ? entry.ctrl.signal : undefined;
      var p = changeRequest(key, entry.qty, signal)
        .then(function (cart) {
          if (pendingChanges.get(key) === entry) pendingChanges.delete(key);
          cartGeneration++;
          return cart;
        })
        .catch(function (err) {
          // Even on abort/error we clear so the submit can proceed
          if (pendingChanges.get(key) === entry) pendingChanges.delete(key);
          cartGeneration++;
          return null;
        });
      promises.push(p);
    });
    return Promise.all(promises);
  }

  /* ---------- Qty change orchestrator ---------- */

  var pendingChanges = new Map(); // key → { qty, ctrl, timer, stableQty }

  // Concurrency guards. cartGeneration is bumped on every authoritative mutation we apply
  // (change.js / add.js response). Any getCart()-based reconcile (defensiveSync, observer)
  // captures the generation before its fetch and DISCARDS its result if the generation
  // advanced or a change is now pending — this stops a stale /cart.js snapshot (read while a
  // qty change was still in flight) from overwriting the newer value, which caused the qty
  // to visibly reset after an increment. suppressBuildSync silences the cart:build self-loop.
  var cartGeneration = 0;
  var suppressBuildSync = 0;

  // Dirty-Line-Schutz: nach einem optimistischen Qty-Patch eine Line ~800 ms als "dirty" markieren.
  // EXTERNE Reconciles (defensiveSync/Observer/cart:updated, Upsell-Apps) überspringen dirty Lines,
  // damit der frisch geklickte Wert nicht zurückspringt. Unsere eigenen (authoritative) Reconciles
  // ignorieren dirty und schreiben den echten Server-Stand.
  var dirtyLines = {};
  var DIRTY_MS = 800;
  function markDirty(key) { if (key) dirtyLines[key] = Date.now(); }
  function isDirty(key) { var t = dirtyLines[key]; return !!t && (Date.now() - t) < DIRTY_MS; }

  /**
   * Braucht der sichtbare Cart ein neues HTML, oder reicht ein JSON-Reconcile?
   *
   * Verglichen werden die Line-Keys, nicht nur deren Anzahl: beim Wechsel des
   * Geschenks bleibt die Anzahl gleich, der Key aendert sich aber — ohne
   * Key-Vergleich bliebe die alte Geschenk-Zeile mit falschem Bild stehen.
   * Eine reine Mengenaenderung laesst den Key unangetastet (er ist
   * variantId + Property-Hash) und braucht deshalb kein neues HTML.
   *
   * Auf den Drawer gescopet: auf /cart existieren Seite und Drawer gleichzeitig,
   * ein globales Zaehlen wuerde jede Zeile doppelt sehen.
   */
  /* ---------- Zeilen-Hinweis (z. B. Lagerbestand gedeckelt) ---------- */

  var noticeTimers = new WeakMap();
  var NOTICE_MS = 6000;

  function showLineNotice(item, text) {
    if (!item || !text) return;
    var el = item.querySelector('[data-line-notice]');
    if (!el) {
      el = document.createElement('div');
      el.className = 'lp-item__notice';
      el.setAttribute('data-line-notice', '');
      el.setAttribute('role', 'status'); // Screenreader bekommen die Deckelung mit
      (item.querySelector('.lp-item__body') || item).appendChild(el);
    }
    el.textContent = text;

    var prev = noticeTimers.get(item);
    if (prev) clearTimeout(prev);
    noticeTimers.set(item, setTimeout(function () {
      noticeTimers.delete(item);
      if (el.parentNode) el.parentNode.removeChild(el);
    }, NOTICE_MS));
  }

  function clearLineNotice(item) {
    if (!item) return;
    var prev = noticeTimers.get(item);
    if (prev) { clearTimeout(prev); noticeTimers.delete(item); }
    var el = item.querySelector('[data-line-notice]');
    if (el && el.parentNode) el.parentNode.removeChild(el);
  }

  function needsHtmlRebuild(cart) {
    var scope = document.querySelector(SEL_DRAWER) || document;
    var nodes = scope.querySelectorAll(SEL_CART_ITEM);
    var lines = (cart && cart.items) ? cart.items : [];
    if (nodes.length !== lines.length) return true;

    var domKeys = {};
    for (var i = 0; i < nodes.length; i++) {
      domKeys[nodes[i].getAttribute('data-key')] = true;
    }
    for (var j = 0; j < lines.length; j++) {
      if (!domKeys[lines[j].key]) return true;
    }
    return false;
  }

  /**
   * Send ONE /cart/change.js for this line and wait for it. While it's in flight the line is
   * locked (entry stays in pendingChanges, button shows is-loading). If the user clicked again
   * meanwhile, entry.qty changed → we send the NEW target before finalizing. This serializes
   * per-line: never two parallel requests for the same line, so responses can't arrive
   * out-of-order and revert each other. The final UI state comes ONLY from the server cart.
   */
  function processLine(key, entry, item, attempt) {
    attempt = attempt || 0;
    var sent = entry.qty; // the quantity we are about to commit
    changeRequest(key, sent)
      .then(function (cart) {
        if (!cart || !cart.items) throw new Error('no-cart'); // → unified failure path im .catch

        cartGeneration++; // authoritative cart applied → invalidate any in-flight getCart reconcile

        // User clicked again while we were loading → commit the newest target first (still locked).
        if (entry.qty !== sent) {
          return processLine(key, entry, item, 0);
        }

        // Done for this line.
        if (pendingChanges.get(key) === entry) pendingChanges.delete(key);
        if (item) item.classList.remove('is-loading');
        clearLineNotice(item); // eine geglueckte Aenderung raeumt den alten Hinweis weg

        // Server-echter Voll-Refresh des sichtbaren Carts (Drawer: XHR-Rerender + Chrome; /cart: Reload).
        // Deckt Mengenänderung, Entfernen, "Du sparst" und Progress-Bar zuverlässig ab.
        settleCart(cart, item);

        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart, __internal: true } }));

        // Auto-cleanup: gift below threshold. Serialized like any other line change.
        var giftLine = findGiftLine(cart);
        var picker = document.querySelector(SEL_PICKER);
        var threshold = picker ? (parseInt(picker.getAttribute('data-gift-threshold-cents'), 10) || 0) : 0;
        if (giftLine && threshold > 0 && paidSubtotalCents(cart) < threshold && !pendingChanges.has(giftLine.key)) {
          var giftEntry = { qty: 0, ctrl: null, timer: null, stableQty: giftLine.quantity };
          pendingChanges.set(giftLine.key, giftEntry);
          changeRequest(giftLine.key, 0).then(function (cart2) {
            if (pendingChanges.get(giftLine.key) === giftEntry) pendingChanges.delete(giftLine.key);
            cartGeneration++;
            settleCart(cart2, item);
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart2, __internal: true } }));
          }).catch(function () {
            if (pendingChanges.get(giftLine.key) === giftEntry) pendingChanges.delete(giftLine.key);
          });
        }
      })
      .catch(function (err) {
        var status = err && err.status;
        var clientError = status >= 400 && status < 500;

        // Transienter Fehler (App-fetch-Wrapper / Rate-Limit / Netz) → 1× erneut versuchen,
        // mit dem aktuellsten Zielwert (entry.qty). Ein 4xx ist dagegen deterministisch
        // (typisch 422: Lagerbestand erschoepft) — erneut senden liefert dieselbe Antwort
        // und verzoegert nur das sichtbare Zuruecksetzen.
        if (!clientError && attempt < 1) { return processLine(key, entry, item, attempt + 1); }

        // NICHT blind auf einen geratenen Altwert zurücksetzen (genau das war das
        // Zurückspringen). Echten Cart laden und daraus reconcilen: ging die Änderung doch
        // durch, bleibt der neue Wert; ging sie nicht durch, zeigt sich korrekt der echte Wert.
        if (pendingChanges.get(key) === entry) pendingChanges.delete(key);
        if (item) item.classList.remove('is-loading');

        var serverMsg = (err && err.body && (err.body.description || err.body.message)) || '';

        getCart().then(function (trueCart) {
          if (!trueCart || !trueCart.items) return;
          cartGeneration++;

          // Erklaeren statt stumm zuruecksetzen: sonst wirkt die gedeckelte Menge
          // wie ein kaputter Mengenschalter.
          if (clientError) {
            var line = null;
            for (var i = 0; i < trueCart.items.length; i++) {
              if (trueCart.items[i].key === key) { line = trueCart.items[i]; break; }
            }
            if (line && line.quantity < sent) {
              showLineNotice(item, 'Nur noch ' + line.quantity + ' Stück verfügbar.');
            } else if (serverMsg) {
              showLineNotice(item, serverMsg);
            }
          }

          settleCart(trueCart, item);
        }).catch(function () { /* offline → optimistischen Wert stehen lassen, kein Sprung */ });
      });
  }

  function applyQtyChange(key, qty, btn) {
    var item = (btn && btn.closest && btn.closest(SEL_CART_ITEM))
               || document.querySelector(SEL_CART_ITEM + '[data-key="' + key + '"]');

    markDirty(key); // schützt den frisch geklickten Wert vor externen Reconciles (~800 ms)

    var existing = pendingChanges.get(key);
    if (existing) {
      // A request for this line is already loading. SERIALIZE: do NOT fire a parallel request
      // (parallel + out-of-order responses are exactly what made the qty jump back). Just record
      // the newest target — processLine commits it once the current request confirms.
      existing.qty = qty;
      if (qty > 0) { patchLineQty(key, qty); syncEphemeralFromDOM(); }
      return;
    }

    // Fresh change for this line. Capture the server-confirmed baseline BEFORE the optimistic patch.
    var stableQty = item ? (parseInt(item.getAttribute('data-qty'), 10) || 0) : 0;

    // Optimistic number for instant feedback (qty > 0 only; for 0 we wait for server removal).
    if (qty > 0) { patchLineQty(key, qty); syncEphemeralFromDOM(); }
    if (item) item.classList.add('is-loading');

    var ctrl = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    var entry = { qty: qty, ctrl: ctrl, timer: null, stableQty: stableQty };
    pendingChanges.set(key, entry); // registered synchronously → reconcile per-line skip sees it

    processLine(key, entry, item);
  }

  /* ---------- Gift CTA handlers ---------- */

  function handleGiftSelect(variantId, currentGiftKey, btn) {
    if (btn) btn.disabled = true;
    var props = {};
    props[GIFT_PROP_KEY] = GIFT_PROP_VAL;

    var removePromise = currentGiftKey ? changeRequest(currentGiftKey, 0) : Promise.resolve();
    return removePromise
      .then(function () { return addRequest(parseInt(variantId, 10), props); })
      .then(function () { return getCart(); })
      .then(function (cart) {
        cartGeneration++;
        settleCart(cart, btn);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart, __internal: true } }));
      })
      .catch(function () { if (btn) btn.disabled = false; });
  }

  function handleGiftRemove(giftKey, btn) {
    if (btn) btn.disabled = true;
    return changeRequest(giftKey, 0)
      .then(function (cart) {
        cartGeneration++;
        settleCart(cart, btn);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart, __internal: true } }));
      })
      .catch(function () { if (btn) btn.disabled = false; });
  }

  /* ---------- Gift size popup ----------
     Multi-variant gifts (e.g. shoe sizes) open a centered popup that shows ALL sizes with
     room — the small card couldn't fit them. The card carries a hidden .lp-gcard__sizes-row
     (data template); we clone its chips into the popup. Single-variant gifts skip the popup. */
  function ensureGiftModal() {
    var drawer = document.querySelector(SEL_DRAWER);
    if (!drawer) return null;
    var modal = drawer.querySelector('[data-gift-modal]');
    if (modal) return modal;
    modal = document.createElement('div');
    modal.className = 'lp-gift-modal';
    modal.setAttribute('data-gift-modal', '');
    modal.innerHTML =
      '<div class="lp-gift-modal__backdrop" data-gift-modal-close></div>' +
      '<div class="lp-gift-modal__panel" role="dialog" aria-modal="true" aria-label="Größe wählen">' +
        '<button type="button" class="lp-gift-modal__close" data-gift-modal-close aria-label="Schließen">×</button>' +
        '<div class="lp-gift-modal__title"></div>' +
        '<div class="lp-gift-modal__sizes" data-gift-modal-sizes></div>' +
      '</div>';
    drawer.appendChild(modal);
    return modal;
  }

  function openGiftModal(card) {
    var modal = ensureGiftModal();
    if (!modal || !card) return;
    var titleEl = card.querySelector('.lp-gcard__title');
    var name = titleEl ? titleEl.textContent.trim() : '';
    var row = card.querySelector('.lp-gcard__sizes-row');
    var modalTitle = modal.querySelector('.lp-gift-modal__title');
    var modalSizes = modal.querySelector('[data-gift-modal-sizes]');
    if (modalTitle) modalTitle.textContent = 'Wähle deine Größe' + (name ? ' – ' + name : '');
    if (modalSizes) modalSizes.innerHTML = row ? row.innerHTML : ''; // chips are server-rendered + already in DOM
    modal.classList.add('is-open');
  }

  function closeGiftModal() {
    var drawer = document.querySelector(SEL_DRAWER);
    var modal = drawer && drawer.querySelector('[data-gift-modal]');
    if (modal) modal.classList.remove('is-open');
  }

  /* ---------- Gutschein / Coupon ---------- */

  function showCouponMsg(scope, msg) {
    var el = scope && scope.querySelector('[data-coupon-msg]');
    if (!el) return;
    if (msg) { el.textContent = msg; el.hidden = false; }
    else { el.textContent = ''; el.hidden = true; }
  }

  function markCouponActive(scope, code, cart) {
    var confirm = scope.querySelector('[data-coupon-confirm]');
    var text = scope.querySelector('[data-coupon-confirm-text]');
    if (text) {
      var amt = (cart && cart.total_discount) ? ' (−' + fmtMoney(cart.total_discount) + ')' : '';
      text.textContent = 'Code ' + String(code).toUpperCase() + ' aktiv' + amt;
    }
    if (confirm) confirm.hidden = false;
    scope.setAttribute('data-coupon-active', '');
    var input = scope.querySelector('[data-coupon-input]');
    if (input) input.value = '';
  }

  // Apply a discount code to the cart via /discount/CODE (server applies + 302).
  // Drawer: refresh in place, keep drawer open. Cart page: reload to show the applied code.
  function applyCoupon(scope) {
    if (!scope) return;
    var input = scope.querySelector('[data-coupon-input]');
    var applyBtn = scope.querySelector('[data-coupon-apply]');
    var code = input ? input.value.trim() : '';
    if (!code) { if (input) input.focus(); return; }
    showCouponMsg(scope, '');
    if (applyBtn) applyBtn.classList.add('is-loading');

    fetch('/discount/' + encodeURIComponent(code), { credentials: 'same-origin' })
      .then(function () { return getCart(); })
      .then(function (cart) {
        if (applyBtn) applyBtn.classList.remove('is-loading');
        var applied = cart && (
          (cart.cart_level_discount_applications && cart.cart_level_discount_applications.length > 0) ||
          (cart.total_discount > 0)
        );
        if (!applied) {
          showCouponMsg(scope, 'Code ungültig oder nicht anwendbar.');
          return;
        }
        cartGeneration++;                 // authoritative cart change
        markCouponActive(scope, code, cart);
        settleCart(cart, scope);          // Drawer: XHR-Rerender + Chrome; /cart: Reload
      })
      .catch(function () {
        if (applyBtn) applyBtn.classList.remove('is-loading');
        showCouponMsg(scope, 'Fehler. Bitte erneut versuchen.');
      });
  }

  /* ---------- Click delegation ---------- */

  function onClick(evt) {
    var t = evt.target;
    if (!t || !t.closest) return;

    // Gift-Dock auf-/zuklappen. Einzige Stelle, die den Zustand aendert —
    // automatisch klappt nichts, das Dock startet offen.
    var giftToggle = t.closest('[data-gift-toggle]');
    if (giftToggle) {
      evt.preventDefault();
      var togglePicker = giftToggle.closest(SEL_PICKER);
      if (!togglePicker) return;
      setGiftDockOpen(togglePicker, !togglePicker.classList.contains('is-open'));
      return;
    }

    // Coupon: anwenden
    var couponApply = t.closest('[data-coupon-apply]');
    if (couponApply) {
      evt.preventDefault();
      applyCoupon(couponApply.closest('[data-coupon]'));
      return;
    }

    var incBtn = t.closest(SEL_QTY_INC);
    if (incBtn) {
      evt.preventDefault();
      var incWrap = incBtn.closest(SEL_QTY_WRAP);
      var incItem = incBtn.closest(SEL_CART_ITEM);
      var incKey = incBtn.getAttribute('data-key') || (incWrap && incWrap.getAttribute('data-key'));
      // Read live data-qty (reflects any pending optimistic state)
      var incQty = parseInt((incItem && incItem.getAttribute('data-qty')) || (incWrap && incWrap.getAttribute('data-qty')) || '1', 10) || 1;
      if (!incKey) return;
      applyQtyChange(incKey, incQty + 1, incBtn);
      return;
    }

    var decBtn = t.closest(SEL_QTY_DEC);
    if (decBtn) {
      evt.preventDefault();
      var decWrap = decBtn.closest(SEL_QTY_WRAP);
      var decItem = decBtn.closest(SEL_CART_ITEM);
      var decKey = decBtn.getAttribute('data-key') || (decWrap && decWrap.getAttribute('data-key'));
      var decQty = parseInt((decItem && decItem.getAttribute('data-qty')) || (decWrap && decWrap.getAttribute('data-qty')) || '1', 10) || 1;
      if (!decKey) return;
      // Decrement clamps at 1 — never auto-deletes via this button.
      // Removal requires an explicit click on the (server-confirmed) trash button.
      // Prevents accidental delete on rapid double-tap.
      var nextQty = Math.max(1, decQty - 1);
      if (nextQty === decQty) return; // already at floor, ignore
      applyQtyChange(decKey, nextQty, decBtn);
      return;
    }

    var trashBtn = t.closest(SEL_TRASH);
    if (trashBtn) {
      evt.preventDefault();
      var tKey = trashBtn.getAttribute('data-key');
      if (!tKey) return;
      applyQtyChange(tKey, 0, trashBtn);
      return;
    }

    var giftRemoveBtn = t.closest(SEL_GIFT_REMOVE);
    if (giftRemoveBtn) {
      evt.preventDefault();
      var gKey = giftRemoveBtn.getAttribute('data-key');
      if (!gKey) return;
      handleGiftRemove(gKey, giftRemoveBtn);
      // Kein Scrollen noetig (Dock sitzt fix im Bottom-Stack) und bewusst auch
      // kein Aufklappen: hat die Kundin zugeklappt, bleibt es zu.
      return;
    }

    // Gift SIZE chip (step 2): commit this exact variant as the gift, then close the popup.
    var sizeBtn = t.closest('[data-gift-size]');
    if (sizeBtn) {
      evt.preventDefault();
      if (sizeBtn.disabled) return;
      var sVariant = sizeBtn.getAttribute('data-variant-id');
      if (!sVariant) return;
      var sPicker = document.querySelector(SEL_PICKER);
      var sCurrentKey = sPicker ? sPicker.getAttribute('data-selected-gift-key') : '';
      handleGiftSelect(sVariant, sCurrentKey, sizeBtn);
      closeGiftModal();
      return;
    }

    // Close the size popup (backdrop or × button).
    if (t.closest('[data-gift-modal-close]')) {
      evt.preventDefault();
      closeGiftModal();
      return;
    }

    var giftCta = t.closest(SEL_GIFT_CTA);
    if (giftCta) {
      evt.preventDefault();
      if (giftCta.disabled) return;
      var action = giftCta.getAttribute('data-action');
      var variantId = giftCta.getAttribute('data-variant-id');
      var giftKey = giftCta.getAttribute('data-gift-key');
      if (action === 'remove' && giftKey) {
        handleGiftRemove(giftKey, giftCta);
      } else if (action === 'select') {
        var card = giftCta.closest('[data-gift-card]');
        // Multi-variant gift (sizes) → open the size popup instead of adding a default variant.
        if (card && card.getAttribute('data-multi') === 'true') {
          openGiftModal(card);
          return;
        }
        if (!variantId) return;
        // Single-variant gift → add directly (no popup).
        var picker = document.querySelector(SEL_PICKER);
        var currentKey = picker ? picker.getAttribute('data-selected-gift-key') : '';
        handleGiftSelect(variantId, currentKey, giftCta);
      }
    }
  }

  /* ---------- cart:updated dispatch handler ---------- */

  function onCartUpdated(evt) {
    // Skip events WE dispatched — applyQtyChange / gift handlers already reconciled with this
    // exact cart. Re-reconciling here is redundant work and a flicker source on rapid clicks.
    if (evt && evt.detail && evt.detail.__internal) return;
    var cart = evt && evt.detail && evt.detail.cart;
    if (cart) {
      // External trigger carrying cart data (e.g., add-to-cart from PDP, theme.CartForm rebuild).
      reconcileFromCart(cart);
    } else {
      // Some external code dispatched without cart payload — sync from DOM.
      syncEphemeralFromDOM();
      updateGiftPicker(null);
    }
  }

  /* ---------- Init ---------- */

  /**
   * Defensive: when ANY external code mutates the cart, sync our drawer state.
   * Different apps (Trusted Shops, Varify, custom buttons) dispatch various event names —
   * we listen to all of them and reconcile. If event detail carries cart data, use it;
   * otherwise fetch /cart.js once and sync.
   */
  function defensiveSync(evt) {
    var cart = evt && evt.detail && evt.detail.cart;
    if (cart) {
      // External payload carries real cart data — reconcileFromCart's per-line skip still
      // protects any in-flight line. (We don't bump cartGeneration here: this isn't a
      // mutation we serialized, just a sync.)
      reconcileFromCart(cart);
      return;
    }
    // Ignore cart:build that WE dispatched in rebuildDrawerHTML (self-loop) and skip starting
    // a read while a qty change is pending — that change reconciles itself on its response.
    if (suppressBuildSync > 0) return;
    if (pendingChanges.size > 0) return;
    // Fall back: refetch authoritative cart, then reconcile — but discard the result if a
    // mutation landed or one became pending while we were fetching (stale-snapshot guard).
    var gen = cartGeneration;
    getCart().then(function (c) {
      if (cartGeneration !== gen || pendingChanges.size > 0) return;
      reconcileFromCart(c);
    }).catch(function () {
      if (cartGeneration !== gen || pendingChanges.size > 0) return;
      // even on fetch error, at least sync the empty state from DOM
      toggleEmptyState(null);
      syncEphemeralFromDOM();
    });
  }

  function init() {
    document.addEventListener('click', onClick);
    document.addEventListener('cart:updated', onCartUpdated);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeGiftModal();
      // Enter im Gutschein-Feld → anwenden (NICHT das Cart-Form submitten / Checkout auslösen)
      if (e.key === 'Enter' && e.target && e.target.matches && e.target.matches('[data-coupon-input]')) {
        e.preventDefault();
        applyCoupon(e.target.closest('[data-coupon]'));
      }
    });

    // Catch-all for external mutations (PDP add-to-cart, sticky CTAs, Varify, etc.)
    // These events are dispatched by various scripts on the theme — we sync on every one.
    // NOTE: 'cart:build' intentionally NOT listed — we dispatch it ourselves in
    // rebuildDrawerHTML(), and reacting to it with a getCart() reconcile created a
    // self-loop that re-introduced the stale-snapshot qty reset. theme.CartForm's own
    // rebuild mutates .cart__items, which the MutationObserver (guarded) already syncs.
    [
      'cart:open',
      'cart:change',
      'cart-drawer:updated',
      'ajaxProduct:added',
      'product:added',
      'quantity-update:after',
      'drawerOpen'
    ].forEach(function (eventName) {
      document.addEventListener(eventName, defensiveSync);
    });

    // Watch the items container for external mutations (theme.CartForm rebuilds, app injections)
    attachItemsObserver();
    // Initial DOM-based sync — server-rendered state is authoritative.
    toggleEmptyState(null);
    syncEphemeralFromDOM();

    // Intercept cart form submissions (Drawer-Form + Cart-Page-Form, both action="/cart")
    // and flush any in-flight qty changes BEFORE the form posts to /cart. Otherwise the
    // stale `updates[]` hidden inputs would override the server cart that our ajax just
    // updated, and the user would land on /checkout with the wrong qty.
    document.querySelectorAll('form[action$="/cart"]').forEach(function (form) {
      form.addEventListener('submit', function (evt) {
        // If another listener (e.g. theme.CartForm terms-confirmation) already blocked
        // the submit, don't override it — we must not bypass terms-acceptance checks.
        if (evt.defaultPrevented) return;

        // Capture any optimistic data-qty that might not yet be reflected in updates[]
        // (defensive — patchLineQty already syncs them, but belt-and-suspenders).
        form.querySelectorAll(SEL_CART_ITEM).forEach(function (item) {
          var input = item.querySelector('input[name="updates[]"]');
          var qty = item.getAttribute('data-qty');
          if (input && qty != null) input.value = qty;
        });

        if (pendingChanges.size === 0) return; // nothing in flight, allow submit

        evt.preventDefault();
        // Disable submit button so user can't click twice during flush
        var submitBtn = form.querySelector('button[type="submit"], button[name="checkout"]');
        if (submitBtn) {
          submitBtn.classList.add('btn--loading');
          submitBtn.disabled = true;
        }

        flushPendingChanges().then(function () {
          // After all qty changes are server-confirmed, submit the form. Re-sync the
          // hidden inputs one more time from the latest data-qty (now reconciled).
          form.querySelectorAll(SEL_CART_ITEM).forEach(function (item) {
            var input = item.querySelector('input[name="updates[]"]');
            var qty = item.getAttribute('data-qty');
            if (input && qty != null) input.value = qty;
          });
          form.submit();
        }).catch(function () {
          // On error: clear loading state, let user retry
          if (submitBtn) {
            submitBtn.classList.remove('btn--loading');
            submitBtn.disabled = false;
          }
        });
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
